import './InterventionRowActions.less';

import { EllipsisOutlined } from '@ant-design/icons';
import { Button, Popover } from 'antd';
import PermissionValidator from 'authorization/permissionValidator';
import TableActionButton from 'components/shared/buttons/TableActionButton';
import AccessiblePopconfirm from 'components/statements/table/AccessiblePopconfirm';
import { ajaxByUser } from 'helper/api';
import { useRootData } from 'hooks/hook';
import ProfiBazaTooltip from 'layout/ProfiBazaTooltip';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { RouteComponentProps } from 'react-router';
import { getProfiBazaApiClient } from 'services/ProfiBazaApi';
import {
	InterventionListDto,
	Permission,
	PublicHealthInterventionStatus,
} from 'services/src/models';

import CloneAction from './CloneAction';
import { getInterventionActionAvailability } from './interventionActionAvailability';

interface IProps extends RouteComponentProps {
	record: InterventionListDto;
	canModify: boolean;
}

const InterventionRowActions: React.FC<IProps> = (props) => {
	const { record, canModify } = props;
	const accountDetailsStore = useRootData(
		(store) => store.accountDetailsStore
	);
	const gridStore = useRootData((store) => store.gridStore);
	const account = accountDetailsStore.account.get();
	const hasPermission = new PermissionValidator(account!).has(
		Permission.RIZPModifications
	);
	const actions = getInterventionActionAvailability(
		record,
		account?.subject?.id,
		canModify,
		hasPermission
	);
	const [visible, setVisible] = useState(false);
	const [interacting, setInteracting] = useState(false);
	const handleInteractionChange = (active: boolean) => {
		setInteracting(active);
		setVisible(active);
	};

	const changeStatus = (action: 'suspend' | 'unsuspend' | 'cancel') => {
		const messages = {
			suspend: 'Zawieszono interwencję',
			unsuspend: 'Odwieszono interwencję',
			cancel: 'Anulowano interwencję',
		};
		ajaxByUser(messages[action], () =>
			getProfiBazaApiClient()
				.then((api) =>
					api.intervention[action]({ body: { id: record.id } })
				)
				.then(() => {
					gridStore.searching.set(true);
				})
		);
	};

	const suspended =
		record.status === PublicHealthInterventionStatus.SUSPENDED;
	const clone = actions.clone ? (
		<CloneAction {...props} onInteractionChange={handleInteractionChange} />
	) : null;
	const statusAction = (
		action: 'suspend' | 'unsuspend' | 'cancel',
		label: string,
		verb: string
	) => (
		<AccessiblePopconfirm
			title={
				<>
					Czy na pewno chcesz {verb} {record.name}?
				</>
			}
			onInteractionChange={handleInteractionChange}
			onConfirm={() => changeStatus(action)}
		>
			<ProfiBazaTooltip title={label}>
				<TableActionButton
					kind={
						action === 'cancel'
							? 'CLOSE'
							: suspended
							? 'DEACTIVATE'
							: 'ACTIVATE'
					}
					label={label}
				/>
			</ProfiBazaTooltip>
		</AccessiblePopconfirm>
	);

	return (
		<div className="intervention-row-actions">
			<span className="intervention-row-actions__slot">
				<ProfiBazaTooltip title="Podgląd">
					<TableActionButton
						kind="DETAILS"
						label="Podgląd"
						linkTo={'/rizp/details/' + record.id}
					/>
				</ProfiBazaTooltip>
			</span>
			<span className="intervention-row-actions__slot">
				{actions.edit && (
					<ProfiBazaTooltip title="Edytuj">
						<TableActionButton
							kind="EDIT"
							label="Edytuj"
							linkTo={'/rizp/edit/' + record.id}
						/>
					</ProfiBazaTooltip>
				)}
			</span>
			<span className="intervention-row-actions__slot">
				{actions.more ? (
					<Popover
						placement="bottomRight"
						trigger={['hover', 'focus', 'click']}
						visible={visible}
						onVisibleChange={(nextVisible) => {
							if (!interacting) setVisible(nextVisible);
						}}
						content={
							<div
								className="intervention-row-actions__more"
								aria-label="Akcje interwencji"
							>
								{clone && (
									<div>
										{clone}
										<span>Sklonuj</span>
									</div>
								)}
								{actions.suspend && (
									<div>
										{statusAction(
											suspended ? 'unsuspend' : 'suspend',
											suspended ? 'Odwieś' : 'Zawieś',
											suspended ? 'odwiesić' : 'zawiesić'
										)}
										<span>
											{suspended ? 'Odwieś' : 'Zawieś'}
										</span>
									</div>
								)}
								{actions.cancel && (
									<div>
										{statusAction(
											'cancel',
											'Anuluj',
											'anulować'
										)}
										<span>Anuluj</span>
									</div>
								)}
							</div>
						}
					>
						<Button
							size="small"
							shape="circle"
							type="primary"
							icon={<EllipsisOutlined />}
							aria-label="Więcej opcji"
							aria-expanded={visible}
						/>
					</Popover>
				) : (
					clone
				)}
			</span>
		</div>
	);
};

export default observer(InterventionRowActions);
